import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  FileText,
  Building2,
  Image as ImageIcon,
  MessageSquareQuote,
  UserRound,
  Layers,
  Settings as SettingsIcon,
  Activity as ActivityIcon,
  LogOut,
  Search as SearchIcon,
  Menu,
  X,
  Sparkles,
} from "lucide-react";

import { ASSETS } from "@/lib/company";
import { authApi, setToken } from "@/lib/api";

const MODULES = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/leads", label: "Leads / Contacts", icon: Users },
  { to: "/admin/pages", label: "Pages / Content", icon: FileText },
  { to: "/admin/projects", label: "Residential Projects", icon: Building2 },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { to: "/admin/team", label: "Team", icon: UserRound },
  { to: "/admin/services", label: "Services", icon: Layers },
  { to: "/admin/media", label: "Media Manager", icon: ImageIcon },
  { to: "/admin/seo", label: "SEO Settings", icon: Sparkles },
  { to: "/admin/settings", label: "Site Settings", icon: SettingsIcon },
  { to: "/admin/activity", label: "Activity Log", icon: ActivityIcon },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    authApi.me().then(setMe).catch(() => setMe(null));
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const current = MODULES.find((m) => location.pathname.startsWith(m.to));
  const pageTitle = current?.label || "Dashboard";

  const doLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    setToken(null);
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-ink-black text-brand-white dark flex">
      {/* Sidebar */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-40 w-[280px] bg-ink-charcoal border-r border-white/6 flex flex-col transition-transform duration-500 ${
          open ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="h-[260px] flex items-center px-6 border-b border-white/6">
          <img src={ASSETS.logo} alt="Minga Architecture" className="h-48 w-auto object-contain" />
        </div>
        <nav className="flex-1 overflow-y-auto admin-scroll py-4 px-3">
          <ul className="space-y-0.5">
            {MODULES.map(({ to, label, icon: Icon }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={to === "/admin/dashboard"}
                  data-testid={`sidebar-${to.split("/").pop()}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-4 py-2.5 text-[13px] uppercase tracking-[0.12em] font-medium rounded-sm transition-all ${
                      isActive
                        ? "bg-brand-yellow/12 text-brand-yellow border-l-2 border-brand-yellow pl-[14px]"
                        : "text-brand-white/80 hover:text-brand-white hover:bg-white/[0.03]"
                    }`
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="px-4 py-4 border-t border-white/6">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 grid place-items-center rounded-sm bg-brand-yellow/12 text-brand-yellow font-semibold">
              {(me?.name || "?").slice(0, 1)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{me?.name || "—"}</p>
              <p className="text-xs text-brand-gray truncate">{me?.role?.toUpperCase()}</p>
            </div>
            <button
              type="button"
              onClick={doLogout}
              aria-label="Logout"
              className="h-9 w-9 grid place-items-center text-brand-gray hover:text-brand-red border border-white/8 hover:border-brand-red rounded-sm transition-colors"
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Backdrop for mobile */}
      {open && (
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="md:hidden fixed inset-0 bg-black/70 z-30"
          aria-label="Close sidebar"
        />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-[86px] bg-ink-charcoal/70 border-b border-white/6 backdrop-blur px-5 md:px-8 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="md:hidden h-10 w-10 grid place-items-center border border-white/10 rounded-sm"
              aria-label="Open sidebar"
            >
              <Menu className="h-4 w-4" />
            </button>
            <div>
              <p className="eyebrow-muted">[ Admin ]</p>
              <h1 className="text-xl md:text-2xl font-semibold tracking-tight">{pageTitle}</h1>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 border border-white/10 rounded-sm px-3 py-2 bg-white/[0.02] max-w-md w-full">
            <SearchIcon className="h-4 w-4 text-brand-gray" />
            <input
              type="text"
              placeholder="Search leads, projects, media\u2026"
              className="bg-transparent outline-none flex-1 text-sm placeholder:text-brand-gray/70"
            />
          </div>
        </header>
        <main className="flex-1 p-5 md:p-8 admin-scroll overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
